import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { AuthProvider, useHasDialer } from '@/lib/auth/AuthProvider'
import { ProtectedRoute } from '@/lib/auth/ProtectedRoute'
import { AppLayout } from '@/components/layout/AppLayout'
import { LoginPage } from '@/pages/LoginPage'
import { DashboardPage } from '@/pages/DashboardPage'
import { ProspectsPage } from '@/pages/ProspectsPage'
import { InboundPage } from '@/pages/InboundPage'
import { WarmLeadsPage } from '@/pages/WarmLeadsPage'
import { SequencesPage } from '@/pages/SequencesPage'
import { AnalyticsLayout } from '@/pages/analytics/AnalyticsLayout'
import { AnalyticsDashboard } from '@/pages/analytics/AnalyticsDashboard'
import { CampaignsView } from '@/pages/analytics/CampaignsView'
import { EmailView } from '@/pages/analytics/EmailView'
import { FunnelView } from '@/pages/analytics/FunnelView'
import { HealthView } from '@/pages/analytics/HealthView'
import { AlertsView } from '@/pages/analytics/AlertsView'
import { VoiceAgentConfig } from '@/pages/VoiceAgentConfig'
import { AIDraftsPage } from '@/pages/AIDraftsPage'
import { IntegrationsPage } from '@/pages/IntegrationsPage'
import { Toaster } from 'sonner'

function DefaultRedirect() {
  const hasDialer = useHasDialer()
  return <Navigate to={hasDialer ? '/dashboard' : '/warm-leads'} replace />
}

function DashboardGuard() {
  const hasDialer = useHasDialer()
  if (!hasDialer) return <Navigate to="/warm-leads" replace />
  return <DashboardPage />
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 0,
      gcTime: 1000 * 60 * 10, // 10 minutes
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: true,
      retry: 1,
    },
    mutations: {
      retry: 1,
    },
  },
})

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route
              element={
                <ProtectedRoute>
                  <AppLayout />
                </ProtectedRoute>
              }
            >
              <Route path="/dashboard" element={<DashboardGuard />} />
              <Route path="/inbound" element={<InboundPage />} />
              <Route path="/warm-leads" element={<WarmLeadsPage />} />
              <Route path="/cold-calling" element={<ProspectsPage />} />
              <Route path="/sequences" element={<SequencesPage />} />
              <Route path="/analytics" element={<AnalyticsLayout />}>
                <Route index element={<AnalyticsDashboard />} />
                <Route path="campaigns" element={<CampaignsView />} />
                <Route path="email" element={<EmailView />} />
                <Route path="funnel" element={<FunnelView />} />
                <Route path="health" element={<HealthView />} />
                <Route path="alerts" element={<AlertsView />} />
              </Route>
              <Route path="/voice-agent" element={<VoiceAgentConfig />} />
              <Route path="/ai-drafts" element={<AIDraftsPage />} />
              <Route path="/integrations" element={<IntegrationsPage />} />
              {/* Redirect old /prospects URL to new /cold-calling */}
              <Route path="/prospects" element={<Navigate to="/cold-calling" replace />} />
            </Route>
            <Route path="/" element={<DefaultRedirect />} />
          </Routes>
          <Toaster position="top-right" richColors closeButton />
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  )
}

export default App
